"use client";

import { useEffect, useEffectEvent, useReducer, useRef } from "react";
import { useQrCodes } from "@/data/repos";
import { type QrOpens, qrSecondsLeft, qrTtlMs } from "@/domain/qrSignIn";
import { useNow, useNowStore } from "@/shell/providers/now";
import { type QrTile, qrTile } from "./qrTile";
export type QrSignIn = {
  tile: QrTile;
  secondsLeft: number;
  show(): void;
  renew(): void;
  close(): void;
};
const CLOSED: QrTile = {
  kind: "closed"
};
export function useQrTile(opens?: QrOpens): QrSignIn {
  const {
    issueQr,
    voidQr
  } = useQrCodes();
  const clock = useNowStore();
  const [tile, dispatch] = useReducer(qrTile, CLOSED);
  const now = useNow(tile.kind === "open" ? "second" : "minute");
  const request = useRef(0);
  const latest = useRef(tile);
  useEffect(() => {
    latest.current = tile;
  });
  const issue = async () => {
    request.current += 1;
    const asked = request.current;
    const answer = await issueQr(opens);
    if (asked !== request.current) {
      if (answer.ok) void voidQr(answer.value.url);
      return;
    }
    dispatch(answer.ok ? {
      type: "issued",
      url: answer.value.url,
      at: clock.get().toISOString()
    } : {
      type: "fail"
    });
  };
  const forget = () => {
    request.current += 1;
    if (latest.current.kind === "open") void voidQr(latest.current.url);
  };
  const secondsLeft = tile.kind === "open" ? qrSecondsLeft(tile.issuedAt, now, opens) : qrTtlMs(opens) / 1000;
  const expired = tile.kind === "open" && secondsLeft === 0;
  const renew = () => {
    dispatch({
      type: "show"
    });
    dispatch({
      type: "expire"
    });
    void issue();
  };
  const onExpired = useEffectEvent(renew);
  const onOpened = useEffectEvent(() => {
    if (latest.current.kind === "issuing") void issue();
    return forget;
  });
  useEffect(() => {
    if (expired) onExpired();
  }, [expired]);
  useEffect(() => onOpened(), []);
  return {
    tile,
    secondsLeft,
    show: () => {
      dispatch({
        type: "show"
      });
      void issue();
    },
    renew,
    close: () => {
      forget();
      dispatch({
        type: "close"
      });
    }
  };
}